import { useSyncExternalStore } from 'react';
import type { PostHog } from 'posthog-js';
import { posthogOptionsForPath } from '@/utils/analyticsGating';

/**
 * PostHog, kept out of the entry chunk.
 *
 * `posthog-js` is 172 KB of the ~6.8 MB entry bundle, and the entry chunk is
 * the one thing measurably on the LCP path: Sentry traces for `/jobs/:jobId`
 * show it downloading for 2,239ms of a 3,627ms LCP on a slow connection, and
 * costing ~554ms of parse/execute on a fast one. `init` was already deferred to
 * idle, but a static `import posthog from 'posthog-js'` meant the module was
 * still parsed on every page load — the deferral moved the network cost of
 * PostHog's own sub-bundles and nothing else.
 *
 * The React bindings can't help here: `posthog-js/react` statically imports
 * `posthog-js`, so importing `PostHogProvider` drags the whole thing back in.
 * The `slim` entry avoids that, but its provider dereferences `client.config`
 * unguarded — passing a not-yet-loaded client white-screens the app, and
 * mounting the provider only once the client arrives would remount the entire
 * tree mid-session. Hence this small store instead: no provider, no remount,
 * and `posthog-js` moves into an async chunk.
 */

let client: PostHog | undefined;
let pending: Promise<void> | undefined;

const subscribers = new Set<() => void>();

const subscribe = (onChange: () => void) => {
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
  };
};

const getSnapshot = () => client;

/**
 * Loads and initialises PostHog. Idempotent — repeat calls return the first
 * promise rather than re-initialising.
 */
export const loadPosthog = (): Promise<void> => {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (!key) return Promise.resolve();
  if (pending) return pending;

  pending = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      // Read once, at init: the SPA never re-inits on navigation, so a visitor
      // who signs in keeps this session's settings and picks the add-ons up on
      // their next load. See `posthogOptionsForPath` for the measurements.
      ...posthogOptionsForPath(window.location.pathname),
      loaded: (ph: PostHog) => {
        // Exposed for the PostHog toolbar.
        Object.assign(window, { posthog: ph });
      },
    });

    client = posthog;
    subscribers.forEach((onChange) => onChange());
  });

  return pending;
};

/**
 * The PostHog client, or `undefined` until it has loaded.
 *
 * Replaces `usePostHog()` from `posthog-js/react`. Consumers re-render once the
 * client lands, so an effect that identifies on `[currUser, posthog]` still
 * fires — it just fires a moment later than it used to.
 */
export const usePosthogClient = (): PostHog | undefined =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

/** Test seam — resets module state between specs. */
export const resetPosthogClientForTests = () => {
  client = undefined;
  pending = undefined;
  subscribers.clear();
};
